<script setup lang="ts">
/**
 * Right-pane chat transcript.
 *
 * Renders three kinds of messages with distinct backgrounds:
 *   - `user`     — outgoing prompt, accent-tinted, right-aligned bubble.
 *   - `assistant`— model response, neutral bubble, may carry partial
 *                  `content` and any number of `tool_calls`. Tool calls
 *                  render as collapsible JSON blocks.
 *   - `tool`     — synthetic tool-result message added when the user
 *                  manually appends one; rendered as a small code block.
 *
 * The error row sits inline between messages so users see API failures
 * without scrolling away from the conversation.
 *
 * Auto-scroll: we watch `messages` length and the last message's content
 * length and pin the scroll container to the bottom on each tick. The
 * user can scroll up freely; we only re-pin when something appended.
 */
import { computed, nextTick, ref, watch } from 'vue';
import type { ToolCall } from '../../lib/openai-client';

export interface ChatUiMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  /** Accumulated content; for streaming assistant rows this grows token by token. */
  content: string;
  /** Accumulated reasoning/thinking (Kimi K2.6 et al). */
  reasoning?: string;
  /** Tool calls produced by the assistant. Accumulated across stream deltas. */
  tool_calls?: ToolCall[];
  /** For `tool` role: which call_id this result responds to. */
  tool_call_id?: string;
  /** Set on the row currently being streamed. */
  streaming?: boolean;
  /** Inline error attached to this message (e.g. mid-stream failure). */
  error?: string;
}

const props = defineProps<{
  messages: ChatUiMessage[];
  /** Banner shown above the thread on global errors (e.g. no key selected). */
  banner: string | null;
}>();

const scroller = ref<HTMLDivElement | null>(null);
const expandedTools = ref<Set<string>>(new Set());

function toolKey(messageId: string, idx: number, suffix: string): string {
  return `${messageId}:${idx}:${suffix}`;
}

function isExpanded(messageId: string, idx: number, suffix: string): boolean {
  return expandedTools.value.has(toolKey(messageId, idx, suffix));
}

function toggleTool(messageId: string, idx: number, suffix: string): void {
  const k = toolKey(messageId, idx, suffix);
  const next = new Set(expandedTools.value);
  if (next.has(k)) next.delete(k);
  else next.add(k);
  expandedTools.value = next;
}

function prettyArgs(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

const isEmpty = computed(() => props.messages.length === 0);

// Auto-pin to bottom whenever the thread grows.
watch(
  () =>
    props.messages
      .map((m) => `${m.id}|${m.content.length}|${(m.tool_calls ?? []).length}|${m.error ?? ''}`)
      .join('~'),
  async () => {
    await nextTick();
    const el = scroller.value;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  },
);
</script>

<template>
  <div ref="scroller" class="h-full overflow-y-auto px-6 py-6">
    <div v-if="props.banner" class="card p-3 border-danger text-danger text-sm mb-4">
      {{ props.banner }}
    </div>

    <div v-if="isEmpty" class="text-center text-muted text-sm mt-20">
      <p>Start a conversation by typing below.</p>
      <p class="mt-2 text-xs">
        Tip: enable <strong>Tool calling</strong> and ask
        “<em>Use the echo_value tool with value gonka-ok.</em>”
      </p>
    </div>

    <ul v-else class="flex flex-col gap-4 max-w-3xl mx-auto">
      <li
        v-for="m in props.messages"
        :key="m.id"
        :class="[
          'rounded-xl border text-sm',
          m.role === 'user'
            ? 'bg-accent/10 border-accent/30 self-end ml-12'
            : m.role === 'assistant'
              ? 'bg-panel border-border mr-12'
              : 'bg-code/60 border-border mx-6',
        ]"
        :data-role="m.role"
      >
        <header class="flex items-center justify-between px-4 pt-3 text-[11px] uppercase tracking-wide text-muted">
          <span>{{ m.role }}<span v-if="m.tool_call_id"> · {{ m.tool_call_id }}</span></span>
          <span v-if="m.streaming" class="text-accent">streaming…</span>
        </header>

        <div v-if="m.reasoning" class="px-4 pt-2">
          <details class="text-xs">
            <summary class="text-muted cursor-pointer">Reasoning</summary>
            <pre class="mt-1 whitespace-pre-wrap text-muted/90 font-mono text-[11px]">{{ m.reasoning }}</pre>
          </details>
        </div>

        <div
          v-if="m.content.length > 0"
          class="px-4 py-2 whitespace-pre-wrap leading-relaxed"
          :class="m.role === 'tool' ? 'font-mono text-xs' : ''"
        >{{ m.content }}</div>

        <!-- Tool calls -->
        <ul v-if="m.tool_calls && m.tool_calls.length > 0" class="px-4 pb-3 flex flex-col gap-2">
          <li
            v-for="(tc, i) in m.tool_calls"
            :key="(tc.id ?? '') + i"
            class="border border-border rounded-md bg-code/60"
          >
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-xs"
              @click="toggleTool(m.id, i, 'tc')"
            >
              <span class="flex items-center gap-2">
                <span class="pill-on">tool_call</span>
                <code>{{ tc.function?.name ?? '(unknown)' }}</code>
                <span v-if="tc.id" class="text-muted">{{ tc.id }}</span>
              </span>
              <span class="text-muted">{{ isExpanded(m.id, i, 'tc') ? '▾' : '▸' }}</span>
            </button>
            <pre
              v-if="isExpanded(m.id, i, 'tc')"
              class="px-3 pb-3 text-[11px] font-mono whitespace-pre-wrap text-ink/90 border-t border-border pt-2"
            >{{ prettyArgs(tc.function?.arguments) }}</pre>
          </li>
        </ul>

        <div v-if="m.error" class="px-4 pb-3 text-xs text-danger" :data-testid="`error-${m.id}`">
          {{ m.error }}
        </div>
      </li>
    </ul>
  </div>
</template>
